import {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import dayjs from 'dayjs'
import BeyondWords from '@beyondwords/player'
import config from '@config'
import styled, { css, useTheme } from 'styled-components'
import SvgIcon from '@components/SvgIcon'
import translate from '@i18n'
import TextToSpeechInfo from '@components/TextToSpeech/TextToSpeechInfo'
import usePageMetadata from '@hooks/usePageMetadata'
import useSubscriptionStatus from '@hooks/useSubscriptionStatus'
import useIsDarkModeInSocialButton from '@hooks/useIsDarkModeInSocialButton'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import debounce from 'lodash.debounce'

const {
  textToSpeech: { projectId, nonBlickPlusSubscriberContentId },
} = config

type TextToSpeechPlayerProps = {
  setPlayerVisibility: (togglePlayer: boolean) => void
}

type PlayerStates = 'playing' | 'paused' | 'stopped' | 'error' | 'loading'

type BeyondWordsPlayer = {
  destroy: () => void
  addEventListener: (event: string, callback: () => void) => void
  removeEventListener: (event: string) => void
  duration: number
  currentTime: number
  playbackState: Omit<PlayerStates, 'loading' | 'error'>
  playbackRate: number
}

type CustomUserInterfaceProps = Pick<
  TextToSpeechPlayerProps,
  'setPlayerVisibility'
> & {
  player: BeyondWordsPlayer
}

const Wrapper = styled.div`
  min-height: 36px;
  display: flex;
  align-items: center;

  a {
    padding: 0;
  }
`

const PlaybackAnchor = styled.a`
  ${({
    theme: {
      typography: {
        bodycopy: {
          xxsmall: { bundledCSS: bodyCopyXXSmallCSS },
        },
      },
      spacing: { spacing4 },
      color: {
        tertiary: { grey800 },
      },
    },
  }) => css`
    cursor: pointer;
    padding-left: ${spacing4};
    ${bodyCopyXXSmallCSS}

    &:hover {
      color: ${grey800};
    }
  `}
`

const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  max-height: 24px;
  min-width: 165px;
  padding-left: ${({ theme }) => theme.spacing.spacing8};
`

const PlayerButton = styled.button<{ isDarkMode: boolean }>`
  ${({
    theme: {
      spacing: { spacing8 },
      color: {
        tertiary: { grey300, grey600, grey400 },
      },
    },
    isDarkMode,
  }) => {
    return css`
      display: flex;
      justify-content: center;
      align-items: center;
      width: 36px;
      height: 36px;
      border: none;
      cursor: pointer;
      margin-right: ${spacing8};
      padding: 0;
      line-height: 0;
      appearance: none;
      user-select: none;
      border-radius: 50%;
      margin: 0;
      background-color: ${isDarkMode ? grey600 : grey300};

      &:hover {
        background-color: ${grey400};
      }
    `
  }}
`

const playbackRates = [
  0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.2, 1.3, 1.4, 1.5, 2, 2.5, 3,
]

const OuterWrapper = styled.div<{
  togglePlayer: PlayerStates
}>`
  ${({ togglePlayer }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    opacity: 1;
    transition: opacity 0.3s ease-in-out;

    ${togglePlayer === 'loading' &&
    css`
      opacity: 0;
    `}
  `}
`

const PlayerWrapper = styled.div<{
  togglePlayer: PlayerStates
  isDarkMode: boolean
}>`
  ${({
    theme: {
      typography: {
        bodycopy: {
          xxsmall: { bundledCSS: bodyCopyXXSmallCSS },
        },
      },
      color: {
        primary: { primary01, primary02 },
        tertiary: { grey100, grey300, grey600, grey800 },
      },
      spacing: { spacing12 },
    },
    togglePlayer,
    isDarkMode,
  }) => css`
    align-items: center;
    background-color: ${isDarkMode ? grey600 : grey300};
    border-radius: 25px;
    color: ${isDarkMode ? primary02 : primary01};
    display: flex;
    justify-content: space-between;
    min-width: 156px;
    padding-right: ${spacing12};
    ${bodyCopyXXSmallCSS}

    ${togglePlayer === 'error' &&
    css`
      color: ${grey800};
      color: ${isDarkMode ? grey100 : grey800};
      font-size: 12px;
      justify-content: center;
      min-height: 36px;
      padding-left: ${spacing12};
      padding-right: ${spacing12};
    `};
  `}
`

const CustomUserInterface = ({
  player,
  setPlayerVisibility,
}: CustomUserInterfaceProps) => {
  const MAX_POPOVER_SCREEN_WIDTH = 516
  const theme = useTheme()
  const { ressort } = usePageMetadata()
  const isDarkMode = useIsDarkModeInSocialButton()
  const [togglePlayer, setTogglePlayer] = useState<PlayerStates>('loading')
  const listenerRegisteredRef = useRef<boolean>(false)
  const [playerDuration, setPlayerDuration] = useState(player.duration)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [playerCurrentTime, setPlayerCurrentTime] = useState(player.currentTime)
  const iconColor = isDarkMode
    ? theme.color.primary.primary02
    : theme.color.primary.primary01
  const handleTogglePlayer = useCallback(
    (playerState: PlayerStates) => {
      player.playbackState = playerState
      setTogglePlayer(playerState)
      if (playerState === 'stopped') {
        setPlayerVisibility(false)
      }
    },
    [player, setPlayerVisibility]
  )
  const trackingOnEnd = useCallback<TrackingFnType>(() => {
    return {
      event: 'audio_player_end',
      analyticsRessort: ressort,
    }
  }, [ressort])

  const handleEndClickTracking = useTracking(trackingOnEnd)

  useEffect(() => {
    if (
      typeof player?.addEventListener === 'function' &&
      !listenerRegisteredRef.current
    ) {
      listenerRegisteredRef.current = true
      player.addEventListener('ContentAvailable', () => {
        setPlayerDuration(player.duration)
        handleTogglePlayer('playing')
      })
      player.addEventListener('CurrentTimeUpdated', () => {
        setPlayerCurrentTime(player.currentTime)
      })
      player.addEventListener('PlaybackEnded', () => {
        handleTogglePlayer('stopped')
        handleEndClickTracking()
      })
      player.addEventListener('NoContentAvailable', () => {
        setTogglePlayer('error')
      })
    }
    return () => {
      listenerRegisteredRef.current = false
      player?.removeEventListener?.('ContentAvailable')
      player?.removeEventListener?.('CurrentTimeUpdated')
      player?.removeEventListener?.('PlaybackEnded')
      player?.removeEventListener?.('NoContentAvailable')
    }
  }, [player, handleTogglePlayer, handleEndClickTracking])

  const maxPlayerWrapperWidthRef = useRef<number>(MAX_POPOVER_SCREEN_WIDTH)
  const [popoverPosition, setPopoverPosition] = useState<
    'top-right' | 'top-left' | 'top-center'
  >('top-left')

  const updatePopoverPosition = useCallback(() => {
    if (
      window.innerWidth >= maxPlayerWrapperWidthRef.current &&
      popoverPosition === 'top-center'
    ) {
      setPopoverPosition('top-left')
    } else if (
      window.innerWidth < maxPlayerWrapperWidthRef.current &&
      popoverPosition === 'top-left'
    ) {
      setPopoverPosition('top-center')
    }
  }, [popoverPosition])

  useEffect(() => {
    updatePopoverPosition()
    const debouncedUpdatePopoverPosition = debounce(updatePopoverPosition, 60)
    window.addEventListener('resize', debouncedUpdatePopoverPosition)
    return () => {
      window.removeEventListener('resize', debouncedUpdatePopoverPosition)
    }
  }, [updatePopoverPosition])
  const handlePlayClick = useCallback(() => {
    return handleTogglePlayer('playing')
  }, [handleTogglePlayer])

  const handlePauseClick = useCallback(() => {
    return handleTogglePlayer('paused')
  }, [handleTogglePlayer])

  const togglePlaybackRate = useCallback(() => {
    const nextIndex = playbackRates.indexOf(playbackRate) + 1
    const nextPlaybackRate = playbackRates[nextIndex] || playbackRates[0]
    setPlaybackRate(nextPlaybackRate)
    player.playbackRate = nextPlaybackRate
  }, [player, playbackRate])

  return (
    <OuterWrapper togglePlayer={togglePlayer}>
      <PlayerWrapper togglePlayer={togglePlayer} isDarkMode={isDarkMode}>
        {(togglePlayer === 'error' && (
          <>{translate('textToSpeechPlayer.error')}</>
        )) ||
          (togglePlayer !== 'loading' && (
            <>
              {(togglePlayer === 'paused' && (
                <PlayerButton
                  aria-label="Play"
                  onClick={handlePlayClick}
                  isDarkMode={isDarkMode}>
                  <SvgIcon iconName="play-solid" size={20} color={iconColor} />
                </PlayerButton>
              )) || (
                <PlayerButton
                  aria-label="Pause"
                  onClick={handlePauseClick}
                  isDarkMode={isDarkMode}>
                  <SvgIcon iconName="pause-solid" size={20} color={iconColor} />
                </PlayerButton>
              )}

              <span>{`${dayjs.duration(playerCurrentTime * 1000).format('mm:ss')} / ${dayjs.duration(playerDuration * 1000).format('mm:ss')}`}</span>
              <PlaybackAnchor
                aria-label={translate('textToSpeechPlayer.playbackRateUp')}
                onClick={
                  togglePlaybackRate
                }>{`${playbackRate}x`}</PlaybackAnchor>
            </>
          ))}
      </PlayerWrapper>
      {togglePlayer !== 'loading' && togglePlayer !== 'error' && (
        <InfoWrapper>
          <TextToSpeechInfo position={popoverPosition} />
        </InfoWrapper>
      )}
    </OuterWrapper>
  )
}

const TextToSpeechPlayer: FunctionComponent<TextToSpeechPlayerProps> = ({
  setPlayerVisibility,
}) => {
  const { id, teaser } = usePageMetadata()
  const playerRef = useRef<HTMLDivElement>(null)
  const [playerInstance, setPlayerInstance] =
    useState<BeyondWordsPlayer | null>(null)
  const { subscriptionStatus } = useSubscriptionStatus()
  const loadSubscriptionPromotionContent =
    !!teaser?.isPlus && subscriptionStatus !== 'subscribed'

  const playerIds = loadSubscriptionPromotionContent
    ? { contentId: nonBlickPlusSubscriberContentId }
    : { sourceId: `${id}` }

  const { contentId, sourceId } = playerIds

  const targetId = `social-beyondwords-player-${sourceId || contentId}`

  useEffect(() => {
    if (playerRef.current && !playerInstance) {
      const player = new BeyondWords.Player({
        logoIconEnabled: false,
        target: `#${targetId}`,
        projectId,
        sourceId,
        contentId,
        showUserInterface: false,
      })

      setPlayerInstance(player)
    }
  }, [playerInstance, id, targetId, sourceId, contentId])

  return (
    <Wrapper>
      <div id={targetId} ref={playerRef} />
      {playerInstance && (
        <CustomUserInterface
          player={playerInstance}
          setPlayerVisibility={setPlayerVisibility}
        />
      )}
    </Wrapper>
  )
}

const MemoizedTextToSpeechPlayer = memo(TextToSpeechPlayer)

MemoizedTextToSpeechPlayer.displayName = 'MemoizedTextToSpeechPlayer'

export default MemoizedTextToSpeechPlayer
